import { useState, useEffect, useRef } from 'react';
import styles from '../BusinessProfileMain.module.css';
import { API_URL } from '../../../utils/auth';
import Auth from '../../../utils/auth';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

const industryOptions = [
  '',
  'Education & Training',
  'Event & Hospitality Services',
  'Financial & Accounting Services',
  'Healthcare & Wellness',
  'Legal Services',
  'Marketing & Creative Services',
  'Professional Consulting',
  'Real Estate & Property Services',
  'Technology & IT Services',
  'Other Services',
];
const yearsInBusinessOptions = [
  '',
  'Less than 1 Year',
  '1-2 Years',
  '3-5 Years',
  '6-10 Years',
  '10+ Years',
];
const serviceDeliveryModelOptions = [
  '',
  'Project-based',
  'Retainer-based',
  'Subscription/Membership',
  'Mixed model',
  'Productized service',
];
const averageClientEngagementLengthOptions = [
  { value: '', label: 'Pick Average Client Engagement Length' },
  { value: 'Less than 3 months', label: 'Less than 3 months' },
  { value: '3-6 months', label: '3-6 months' },
  { value: '6-12 months', label: '6-12 months' },
  { value: '1-2 years', label: '1-2 Years' },
  { value: '2+ years', label: '2+ Years' },
  { value: 'Varies significantly', label: 'Varies significantly' },
];
const currentTeamStructureOptions = [
  '',
  'Just me (solopreneur)',
  'Small team (2-5 people)',
  'Growing team (6-15 people)',
  'Established team (16-50 people)',
  'Large organization (50+ people)',
  'Enterprise (100+ people)',
];
const currentGrowthStateOptions = [
  '',
  'Startup phase',
  'Growth phase',
  'Optimization phase',
  'Maturity phase',
  'Transformation phase',
];

const BusinessProfileConfiguration = () => {
  const [fields, setFields] = useState({
    industry: '',
    yearsInBusiness: '',
    ServiceDeliveryModel: '',
    averageClientEngagementLength: '',
    currentTeamStructure: '',
    currentGrowthState: '',
  });
  const [serviceTypes, setServiceTypes] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProgram, setDeletingProgram] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const inputRefs = useRef([]);
  const workspaceId = Auth.getWorkspaceId();
  const userId = Auth.getUser()?.id;

  // Fetch configuration and programs on mount
  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/business-profile-configuration/business-profile-configuration`, {
          params: { workspaceId, userId }
        });
        if (data && !data.error) {
          setFields({
            industry: data.industry || '',
            yearsInBusiness: data.years_in_business || '',
            ServiceDeliveryModel: data.service_delivery_model || '',
            averageClientEngagementLength: data.average_client_engagement_length || '',
            currentTeamStructure: data.current_team_structure || '',
            currentGrowthState: data.current_growth_state || '',
          });
        }
      } catch (err) {
        // No config found or error
      } finally {
        setLoading(false);
      }
    };

    const fetchPrograms = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/programs/programs`, { params: { workspaceId, userId } });
        setServiceTypes(data.map(p => ({ id: p.id, description: p.description, isNew: false })));
      } catch (err) {
        setServiceTypes([]);
      }
    };

    if (workspaceId && userId) {
      fetchConfig();
      fetchPrograms();
    }
    // eslint-disable-next-line
  }, [workspaceId, userId]);

  // Add new empty input
  const addServiceType = () => {
    if (adding) return;
    setServiceTypes(prev => ([...prev, { id: null, description: '', isNew: true }]));
    setAdding(true);
    setTimeout(() => {
      if (inputRefs.current[serviceTypes.length]) {
        inputRefs.current[serviceTypes.length].focus();
      }
    }, 100);
  };

  // Handle input change
  const updateServiceType = (index, value) => {
    setServiceTypes(prev => prev.map((item, i) => i === index ? { ...item, description: value } : item));
  };

  // Cancel adding new input
  const handleCancelAdd = () => {
    setServiceTypes(prev => prev.filter(item => !item.isNew));
    setAdding(false);
  };

  // Handle delete program
  const handleDeleteProgram = (program) => {
    setDeletingProgram(program);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingProgram) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/programs/programs`, {
        params: {
          workspaceId,
          userId,
          programId: deletingProgram.id
        }
      });
      
      // Remove from local state
      setServiceTypes(prev => prev.filter(item => item.id !== deletingProgram.id));
      toast.success('Program deleted successfully!');
    } catch (err) {
      // Handle specific error types with proper error codes
      const errorCode = err.response?.data?.errorCode;
      const errorMessage = err.response?.data?.error;
      
      switch (errorCode) {
        case 'FOREIGN_KEY_CONSTRAINT':
        case 'PROGRAM_IN_USE':
          toast.error('Cannot delete program: It is currently assigned to one or more clients. Please reassign or remove the clients from this program first.');
          break;
        case 'NOT_FOUND':
          toast.error('Program not found or already deleted.');
          break;
        case 'PERMISSION_DENIED':
          toast.error('You do not have permission to delete this program.');
          break;
        default:
          // Fallback to string matching for backward compatibility
          if (errorMessage?.toLowerCase().includes('foreign key constraint') || 
              errorMessage?.toLowerCase().includes('program in use')) {
            toast.error('Cannot delete program: It is currently assigned to one or more clients. Please reassign or remove the clients from this program first.');
          } else {
            toast.error(`Error deleting program: ${errorMessage || 'Unknown error occurred'}`);
          }
      }
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeletingProgram(null);
    }
  };

  // Save new program individually
  const handleSaveNewProgram = async (index) => {
    const newItem = serviceTypes[index];
    if (!newItem || !newItem.isNew || !newItem.description.trim()) {
      toast.error('Please enter a program description');
      return;
    }

    if (!workspaceId || !userId) {
      toast.error('Missing required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/programs/programs`, {
        workspaceId,
        userId,
        description: newItem.description.trim(),
      });
      
      // Re-fetch the list after adding
      const { data: programsData } = await axios.get(`${API_URL}/programs/programs`, { params: { workspaceId, userId } });
      setServiceTypes(programsData.map(p => ({ id: p.id, description: p.description, isNew: false })));
      setAdding(false);
      toast.success('Program added successfully!');
    } catch (err) {
      const errorCode = err.response?.data?.errorCode;
      const errorMessage = err.response?.data?.error;
      
      switch (errorCode) {
        case 'DUPLICATE_PROGRAM':
          toast.error('A program with this description already exists.');
          break;
        case 'INVALID_DESCRIPTION':
          toast.error('Program description is invalid or too long.');
          break;
        case 'PERMISSION_DENIED':
          toast.error('You do not have permission to create programs.');
          break;
        default:
          toast.error(`Error saving program: ${errorMessage || 'Unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        workspaceId,
        userId,
        industry: fields.industry,
        yearsInBusiness: fields.yearsInBusiness,
        ServiceDeliveryModel: fields.ServiceDeliveryModel,
        averageClientEngagementLength: fields.averageClientEngagementLength,
        currentTeamStructure: fields.currentTeamStructure,
        currentGrowthState: fields.currentGrowthState,
      };
      const { data } = await axios.put(
        `${API_URL}/business-profile-configuration/business-profile-configuration`,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data && !data.error) {
        toast.success('Profile configuration updated!');
      } else {
        toast.error(data.error || 'Error updating configuration');
      }
    } catch (err) {
      const errorCode = err.response?.data?.errorCode;
      const errorMessage = err.response?.data?.error;
      
      switch (errorCode) {
        case 'VALIDATION_ERROR':
          toast.error('Please check your input and try again.');
          break;
        case 'PERMISSION_DENIED':
          toast.error('You do not have permission to update this configuration.');
          break;
        case 'NOT_FOUND':
          toast.error('Configuration not found.');
          break;
        default:
          toast.error(`Error updating configuration: ${errorMessage || 'Unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.profileSection}>
      <form onSubmit={handleSubmit}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.businessProfileHeader}>Business Profile Configuration</h2>
        </div>
        <div className={styles.infoText}>
          <p>
            This information trains your AI agent to think like an expert consultant who deeply understands
            your specific business model, industry dynamics, and client relationships. The more context you
            provide, the more precise and actionable your weekly insights become - transforming generic
            suggestions into strategic recommendations tailored exactly to how you operate and what drives
            success in your unique market.
          </p>
        </div>
        <div className={styles.formGroup}>
          <label>Industry</label>
          <select
            name="industry"
            value={fields.industry}
            onChange={handleChange}
            className={`${styles.formControl} ${styles.configSelect}`}
          >
            {industryOptions.map(opt => (
              <option key={opt} value={opt}>{opt || 'Pick Industry'}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Years in Business</label>
          <select
            name="yearsInBusiness"
            value={fields.yearsInBusiness}
            onChange={handleChange}
            className={`${styles.formControl} ${styles.configSelect}`}
          >
            {yearsInBusinessOptions.map(opt => (
              <option key={opt} value={opt}>{opt || 'Select Years in Business'}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Service Delivery Model</label>
          <select
            name="ServiceDeliveryModel"
            value={fields.ServiceDeliveryModel}
            onChange={handleChange}
            className={`${styles.formControl} ${styles.configSelect}`}
          >
            {serviceDeliveryModelOptions.map(opt => (
              <option key={opt} value={opt}>{opt || 'Pick Service Delivery Model'}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Average Client Engagement Length</label>
          <select
            name="averageClientEngagementLength"
            value={fields.averageClientEngagementLength}
            onChange={handleChange}
            className={`${styles.formControl} ${styles.configSelect}`}
          >
            {averageClientEngagementLengthOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Current Team Structure</label>
          <select
            name="currentTeamStructure"
            value={fields.currentTeamStructure}
            onChange={handleChange}
            className={`${styles.formControl} ${styles.configSelect}`}
          >
            {currentTeamStructureOptions.map(opt => (
              <option key={opt} value={opt}>{opt || 'Pick Current Team Structure'}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Current Growth Stage</label>
          <select
            name="currentGrowthState"
            value={fields.currentGrowthState}
            onChange={handleChange}
            className={`${styles.formControl} ${styles.configSelect}`}
          >
            {currentGrowthStateOptions.map(opt => (
              <option key={opt} value={opt}>{opt || 'Pick Current Growth Stage'}</option>
            ))}
          </select>
        </div>

        <div className={`${styles.formGroup} ${styles.configFormGroup}`}>
          <label>Programs / Service Types (optional)</label>
          <p className={styles.formText}>
            Below you can enter a short description for the various programs and/or services you provide. 
            This will allow you to sort client lists and reports based on what they have purchased.
          </p>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <>
              {serviceTypes.length === 0 && !adding && (
                <div className={styles.serviceTypeRow}>
                  <span style={{ color: '#888', fontStyle: 'italic' }}>No programs yet.</span>
                  <button
                    type="button"
                    onClick={addServiceType}
                    className={styles.addServiceBtn}
                  >
                    + Add
                  </button>
                </div>
              )}
              {serviceTypes.length > 0 && (
                <div className={styles.programListContainer}>
                  {serviceTypes.map((item, index) => (
                    <div key={item.id || index} className={styles.programRow}>
                      <input
                        type="text"
                        placeholder="Example: Level 1 Program (max characters 40)"
                        value={item.description}
                        onChange={e => updateServiceType(index, e.target.value)}
                        className={styles.formControl}
                        ref={el => inputRefs.current[index] = el}
                        disabled={!item.isNew}
                      />
                      {item.isNew ? (
                        <div className={styles.actionButtons}>
                          <button
                            type="button"
                            onClick={() => handleSaveNewProgram(index)}
                            className={styles.addServiceBtn}
                            disabled={!item.description.trim()}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelAdd}
                            className={styles.removeServiceBtn}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className={styles.actionButtons}>
                          <button
                            type="button"
                            onClick={() => handleDeleteProgram(item)}
                            className={styles.deleteProgramBtn}
                            title="Delete program"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {serviceTypes.length > 0 && !adding && (
                <div className={styles.addProgramButtonContainer}>
                  <button
                    type="button"
                    onClick={addServiceType}
                    className={styles.addServiceBtn}
                  >
                    + Add
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.updateSection}>
          <button className={styles.updateBtn} type="submit" disabled={loading}>{loading ? 'Saving...' : 'Update Information'}</button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle>Delete Program</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deletingProgram?.description}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <button 
            onClick={() => setShowDeleteModal(false)} 
            className={styles.cancelProfileImageBtn}
            disabled={deleting}
          >
            Cancel
          </button>
          <button 
            onClick={handleConfirmDelete} 
            className={styles.deleteProfileImageBtn}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BusinessProfileConfiguration; 